import { useState } from "react";
import { createContext } from "react";
import { LOGIN_URL, DECODE_URL, REFRESH_URL, LOGOUT_URL } from "./paths";
import { apiFetch } from "../utils/apiFetch";
import { captureClientError } from "../utils/captureClientError";

const AuthContext = createContext({});

const COOKIE_NAME = "keepersPhrase";
const REFRESH_STORAGE_KEY = "mgs.admin.refreshToken";
const ACCESS_STORAGE_KEY = "mgs.admin.accessToken";

/** Access 12h no backend — renovar a cada 3h em silêncio. */
export const SILENT_REFRESH_INTERVAL_MS = 3 * 60 * 60 * 1000;
const BOOTSTRAP_RETRY_MS = [500, 1500, 3000, 6000];

/** Deduplica refreshes concorrentes no mesmo tab. */
let refreshInFlightPromise = null;

const readCookieLast = (name) => {
    const cookies = document.cookie.split(";");
    let found = null;
    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.startsWith(name + "=")) {
            const raw = cookie.substring(name.length + 1);
            try {
                found = decodeURIComponent(raw);
            } catch {
                found = raw;
            }
        }
    }
    return found;
};

const clearPathScopedRefreshCookies = () => {
    const expired = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1993 00:00:00 UTC; Secure; SameSite=Lax`;
    document.cookie = `${expired}; Path=/`;
    const path = window.location?.pathname || "/";
    const segments = path.split("/").filter(Boolean);
    let acc = "";
    for (const seg of segments) {
        acc += `/${seg}`;
        document.cookie = `${expired}; Path=${acc}`;
    }
    for (const p of ["/manafix", "/manafix/users", "/manafix/products", "/manafix/serials"]) {
        document.cookie = `${expired}; Path=${p}`;
    }
};

const lsGet = (key) => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const lsSet = (key, value) => {
    if (!value || typeof value !== "string") return;
    try {
        localStorage.setItem(key, value);
    } catch {
        /* ignore */
    }
};

const lsRemove = (key) => {
    try {
        localStorage.removeItem(key);
    } catch {
        /* ignore */
    }
};

const clearClientSession = () => {
    lsRemove(REFRESH_STORAGE_KEY);
    lsRemove(ACCESS_STORAGE_KEY);
    clearPathScopedRefreshCookies();
};

const readStoredRefresh = () => lsGet(REFRESH_STORAGE_KEY) || readCookieLast(COOKIE_NAME);

const readStoredAccess = () => lsGet(ACCESS_STORAGE_KEY);

const persistSessionTokens = ({ accessToken, refreshToken }) => {
    if (accessToken) lsSet(ACCESS_STORAGE_KEY, accessToken);
    if (refreshToken) lsSet(REFRESH_STORAGE_KEY, refreshToken);
};

/** exp do JWT em ms; 0 se inválido. */
const jwtExpMs = (token) => {
    try {
        const part = String(token).split(".")[1];
        if (!part) return 0;
        const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
        return typeof json.exp === "number" ? json.exp * 1000 : 0;
    } catch {
        return 0;
    }
};

const isAccessUsable = (token) => {
    const exp = jwtExpMs(token);
    return Boolean(token) && exp > Date.now() + 120000;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState("loading");
    const [message, setMessage] = useState(null);

    const decodeToken = async (accessToken, { silent = false } = {}) => {
        try {
            const response = await apiFetch(DECODE_URL, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            const raw = await response.text();
            let data = {};
            if (raw) {
                try {
                    data = JSON.parse(raw);
                } catch (parseErr) {
                    throw parseErr;
                }
            }
            if (response.ok && data.user) {
                const { name, birth, email, verified, roles, store } = data.user;
                setUser({ name, birth, email, verified, roles, store, token: accessToken });
                persistSessionTokens({ accessToken });
                if (!silent) setStatus(data.type || "success");
                else setStatus((prev) => (prev === "loading" ? (data.type || "success") : prev));
                return true;
            }
            if (!silent) {
                setMessage(data.message || "Falha ao validar sessão");
                setStatus(data.type === "error" || !response.ok ? "error" : "idle");
            }
            return false;
        } catch (error) {
            captureClientError(error, { area: "auth", op: "decodeToken" });
            if (!silent) {
                setMessage("Erro na comunicação com o servidor");
                setStatus("error");
            }
            return false;
        }
    };

    const login = async (body) => {
        setStatus("loading");
        const { email, password } = body;
        try {
            const response = await apiFetch(LOGIN_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: email?.toLowerCase(), password }),
            });
            const data = await response.json();

            if (data.accessToken) {
                persistSessionTokens({
                    accessToken: data.accessToken,
                    refreshToken: data.refreshToken,
                });
                await decodeToken(data.accessToken);
                return;
            }
            setMessage(data.message);
            setStatus(data.type);
            return data;
        } catch (error) {
            captureClientError(error, { area: "auth", op: "login" });
            setMessage("Erro na comunicação com o servidor");
            setStatus("error");
        }
    };

    const logout = async () => {
        setStatus("loading");
        try {
            if (user?.token) {
                const refreshToken = readStoredRefresh();
                await apiFetch(LOGOUT_URL, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.token}`,
                    },
                    body: JSON.stringify(refreshToken ? { refreshToken } : {}),
                });
            }
        } catch (error) {
            captureClientError(error, { area: "auth", op: "logout" });
        }
        clearClientSession();
        setUser(null);
        setStatus("idle");
    };

    const retrieveRefreshToken = async (legacyToken, opts = {}) => {
        const silent = Boolean(opts && opts.silent);

        if (refreshInFlightPromise) {
            return refreshInFlightPromise;
        }

        refreshInFlightPromise = (async () => {
            try {
                if (!silent) setStatus("loading");

                const attemptRefresh = async (token) => {
                    const body = token ? { refreshToken: token } : {};
                    const response = await apiFetch(REFRESH_URL, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(body),
                    });
                    if (response.status >= 500) {
                        const err = new Error(`refresh ${response.status}`);
                        err.status = response.status;
                        throw err;
                    }
                    const data = await response.json();
                    data._httpStatus = response.status;
                    return data;
                };

                const runAttempts = async () => {
                    const leftover = legacyToken || readStoredRefresh();
                    let data = await attemptRefresh(leftover || undefined);
                    if (!data.accessToken && leftover) {
                        data = await attemptRefresh(undefined);
                    }
                    return { data, leftover };
                };

                let result;
                let lastError = null;
                const delays = silent ? [800] : BOOTSTRAP_RETRY_MS;
                for (let i = 0; i <= delays.length; i++) {
                    try {
                        result = await runAttempts();
                        lastError = null;
                        break;
                    } catch (err) {
                        lastError = err;
                        if (i < delays.length) await sleep(delays[i]);
                    }
                }
                if (lastError) throw lastError;

                const { data, leftover } = result;

                if (data.accessToken) {
                    persistSessionTokens({
                        accessToken: data.accessToken,
                        refreshToken: data.refreshToken || leftover,
                    });
                    return decodeToken(data.accessToken, { silent });
                }

                const invalid =
                    data._httpStatus === 401 || /invalid refresh/i.test(data.message || "");
                if (!silent) {
                    if (invalid || !leftover) {
                        clearClientSession();
                    }
                    setUser(null);
                    setMessage(null);
                    setStatus("idle");
                }
                return data;
            } catch (error) {
                captureClientError(error, { area: "auth", op: "retrieveRefreshToken" });
                if (!silent) {
                    const access = readStoredAccess();
                    if (access && isAccessUsable(access)) {
                        const ok = await decodeToken(access, { silent: false });
                        if (ok) return true;
                    }
                    setMessage("Erro na comunicação com o servidor. Atualize a página.");
                    setStatus("idle");
                }
            } finally {
                refreshInFlightPromise = null;
            }
        })();

        return refreshInFlightPromise;
    };

    /**
     * Bootstrap: access local válido → /who; senão refresh; senão login.
     * Não depende de cookie HttpOnly.
     */
    const bootstrapSession = async () => {
        setStatus("loading");
        const access = readStoredAccess();
        if (access && isAccessUsable(access)) {
            const ok = await decodeToken(access, { silent: false });
            if (ok) {
                retrieveRefreshToken(undefined, { silent: true });
                return true;
            }
        }
        const refresh = readStoredRefresh();
        if (refresh) {
            return retrieveRefreshToken(refresh, { silent: false });
        }
        setUser(null);
        setStatus("idle");
        return false;
    };

    const getCookie = (name) => {
        if (name === COOKIE_NAME) return readStoredRefresh();
        return readCookieLast(name);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                setUser,
                login,
                logout,
                getCookie,
                retrieveRefreshToken,
                bootstrapSession,
                status,
                setStatus,
                message,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
